<template>
  <div class="min-h-screen bg-gray-100 px-3 md:px-6 py-3">
    <div class="bg-white w-full rounded-lg shadow">
      <!-- Header -->
      <div class="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 md:px-6 py-3">
        <div class="flex items-center gap-3">
          <button
            :class="$colors.primary"
            class="rounded-full flex justify-center items-center w-9 h-9"
            @click="RouterBack()"
          >
            <i class="material-icons">chevron_left</i>
          </button>
          <h1 class="text-lg md:text-xl font-bold text-green-700">
            {{ $t('shipping_details') }}
          </h1>
        </div>
      </div>

      <div id="detail" class="px-4 md:px-6 py-4">
        <!-- Cards grid -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <!-- Detail card -->
          <div class="flex shadow w-full h-auto p-4 rounded-lg">
            <div class="flex w-full gap-4">
              <span class="rounded-full h-12 w-12 bg-gray-100 shrink-0">
                <i class="material-icons p-3 text-green-400">list_alt</i>
              </span>
              <div class="w-full space-y-2">
                <div class="text-base font-semibold text-gray-700">
                  {{ $t('detail') }}
                </div>

                <div class="text-sm text-gray-600">
                  {{ $t('shipping_address') }}:
                  <span class="inline-block text-xs font-semibold text-gray-700">
                    {{ formData.address }}
                  </span>
                </div>

                <div v-if="formData.zone && formData.country!='MA'" class="text-sm text-gray-600">
                  {{ $t('zone_shipping') }}:
                  <span class="inline-block text-xs font-semibold text-gray-700">
                    {{ formData.zone.name }}
                  </span>
                </div>

                <div class="text-sm text-gray-600">
                  {{ $t('shipping_date') }}:
                  <span class="inline-block text-xs font-semibold text-gray-700">
                    {{ formData.date }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Customer card -->
          <div class="flex shadow w-full h-auto p-4 rounded-lg">
            <div class="flex w-full gap-4">
              <span class="rounded-full h-12 w-12 bg-gray-100 shrink-0">
                <i class="material-icons p-3 text-green-400">person</i>
              </span>
              <div class="w-full">
                <div class="text-base font-semibold text-gray-700">
                  {{ $t('customer_information') }}
                </div>

                <div class="mt-2 space-y-2">
                  <div class="text-sm text-gray-700 flex items-start gap-2">
                    <i class="material-icons text-gray-400 text-base">person</i>
                    <span class="text-xs font-semibold text-gray-700">
                      {{ formData.order.customer.fullName }}
                    </span>
                  </div>

                  <div class="text-sm text-gray-700 flex items-start gap-2">
                    <i class="material-icons text-gray-400 text-base">room</i>
                    <span class="text-xs font-semibold text-gray-700">
                      {{ formData.order.customer.address }}
                    </span>
                  </div>

                  <div class="text-sm text-gray-700 flex items-start gap-2">
                    <i class="material-icons text-gray-400 text-base">phone</i>
                    <span class="text-xs font-semibold text-gray-700">
                      {{ formData.order.customer.phone }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- (مكان فارغ للكوريي/معلومات إضافية إن احتجتيهم) -->
        </div>

        <!-- Products table -->
        <div class="mt-5 md:mt-6">
          <div class="shadow w-full h-auto p-4 rounded-lg">
            <div class="flex items-center gap-3 mb-3">
              <span class="rounded-full h-10 w-10 bg-gray-100 shrink-0">
                <i class="material-icons p-2.5 text-green-400">local_mall</i>
              </span>
              <div class="text-base font-semibold text-gray-700">
                {{ $t('products_order') }}
              </div>
            </div>

            <div class="w-full overflow-x-auto">
              <table class="min-w-full leading-normal text-sm">
                <thead>
                  <tr>
                    <th class="px-4 md:px-5 py-2 md:py-3 border-b-2 border-gray-200 text-left text-2xs md:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {{ $t('image') }}
                    </th>
                    <th class="px-4 md:px-5 py-2 md:py-3 border-b-2 border-gray-200 text-left text-2xs md:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {{ $t('product') }}
                    </th>
                    <th class="px-4 md:px-5 py-2 md:py-3 border-b-2 border-gray-200 text-left text-2xs md:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {{ $t('quantity') }}
                    </th>
                    <th class="px-4 md:px-5 py-2 md:py-3 border-b-2 border-gray-200 text-left text-2xs md:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {{ $t('price') }}
                    </th>
                  </tr>
                </thead>

                <tbody v-for="(item, index) in formData.order.details" :key="item._id">
                  <tr :class="index % 2 == 0 ? 'bg-gray-50' : ''">
                    <td class="px-4 md:px-5 py-2 border-b border-gray-200 bg-white align-middle">
                      <img
                        :src="item.product.picture || 'https://storeino.b-cdn.net/dev/templates/images/no-image.jpeg'"
                        class="h-14 w-14 md:h-16 md:w-16 object-cover rounded-md"
                      />
                    </td>

                    <td class="px-4 md:px-5 py-2 border-b border-gray-200 bg-white">
                      <div class="text-gray-800 truncate md:whitespace-normal">
                        {{ item.product.name }}
                      </div>
                    </td>

                    <td class="px-4 md:px-5 py-2 border-b border-gray-200 bg-white">
                      <span class="font-semibold text-gray-800">
                        {{ item.quantity }}
                      </span>
                    </td>

                    <td class="px-4 md:px-5 py-2 border-b border-gray-200 bg-white">
                      <span class="text-gray-800 font-semibold">
                        {{ item.unitPrice }}
                      </span>
                      <sup class="uppercase">{{ currentCurrency }}</sup>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- User history -->
        <div
          v-if="$f.getAccessPermission(currentUser, 'userHistories', 'read')"
          class="mt-5 md:mt-6"
        >
          <div class="shadow w-full h-auto p-4 rounded-lg">
            <div class="flex items-center gap-3 mb-3">
              <span class="rounded-full h-10 w-10 bg-gray-100 shrink-0">
                <i class="material-icons p-2.5 text-green-400">history</i>
              </span>
              <div class="text-base font-semibold text-gray-700">
                Info History
              </div>
            </div>

            <div class="w-full overflow-x-auto">
              <div-table
                :config="configListUserHistory"
                :data="dataHistories"
                :loading="loading"
                :paginate="paginate"
                :DivFilter="false"
                :idWharhouse="idWharhouse"
                :currentUser="currentUser"
              />
            </div>
          </div>
        </div>

        <!-- Shippings history -->
        <div v-if="$f.getAccessPermission(currentUser, 'shippingsHistories', 'read')" class="mt-5 md:mt-6">
          <div class="shadow w-full h-auto p-4 rounded-lg">
            <div class="w-full overflow-x-auto">
              <div-table
                :config="configList"
                :data="shippingsHistories"
                :idWharhouse="idWharhouse"
                :currentUser="currentUser"
                :loading="loading"
                :paginate="paginate"
                @paginatation="paginatation"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- bottom padding spacer for mobile -->
      <div class="h-6 md:h-8"></div>
    </div>
  </div>
</template>

<script>
import config from './configHistories'
import configUserHistory from '../users/confighistory';
export default {
  props: {
     currentUser: { type: Object},
     idWharhouse: { type: Object},
   },
  data() {
    return {
      configList: config.list,
      configListUserHistory: configUserHistory.list,
      dataHistories: [],
      loading:false,
      shippingsHistories:[],
       formData: {
            order:{
                customer: {
                    fullName:""
                },
                seller:{}
            },
            zone:{},
            courier:{}   
      },
      show_modal: false,
      content: [],
      currentCurrency:"",
      //currentUser: {},
       paginate: {
        total: 0,
        currentpage: 1,
        lastpage: 1,
        per_page: 0,
       },
    };
  },
  async mounted() {
     if (this.$route.params.id) {
        const editData = await this.$server.get("shippings", {
            id: this.$route.params.id,
        });
        this.formData = editData.content;
         this.formData.date = this.$moment(this.formData.date).format('yyyy-MM-DD');
        this.formData.order.date = this.$moment(this.formData.order.date).format('yyyy-MM-DD HH:mm');
        await this.getCurrencyBycontry( this.formData.country);
        if (this.$f.getAccessPermission(this.currentUser, 'userHistories', 'read')) await this.getUserHistory({ 'model.id': this.$route.params.id });
        if (this.currentUser.type === "Seller") {
         let userI = this.configList.columns.map(function (e) {return e.name;}).indexOf('User');
         if(userI!=-1)  this.configList.columns.splice(userI, 1); 
         let userI2 = this.configList.columns.map(function (e) {return e.name;}).indexOf('Ip Address');
         if(userI2!=-1)  this.configList.columns.splice(userI2, 1);              
     }
     }
    
   // await this.getUser();
   if (this.$f.getAccessPermission(this.currentUser, 'shippingsHistories', 'read')) await this.getShippingHistories();
  },
  methods: {
    RouterBack: function() {
      this.$router.back();
    },
    async getCurrencyBycontry(country){
          const resW = await this.$server.get("warehouses", { country: country}); 
          this.currentCurrency=resW.content.currency;
    },
    async getUser() {
      const res = await this.$me;
      this.currentUser = res.content;
    },
    /*******************************************Modal (View) update Chapping address and Status Order ************************************/
    async getShippingHistories() {
      const filter = {
        shipping: this.$route.params.id,
      };
      const res = await this.$server.search("shippingsHistories", filter);
      if (res && res.content.results) {
        this.shippingsHistories = res.content.results;
        this.shippingsHistories.total = res.content.total;
        this.shippingsHistories.lastpage = res.content.last_page;
        this.shippingsHistories.per_page = res.content.per_page;
        this.shippingsHistories.currentpage = res.content.current_page;
      } else this.shippingsHistories = [];
    },
    async paginatation(direction){
      var page=0;
      if(direction == "next"){
         page=++this.paginate.currentpage;
      }else{
         page=--this.paginate.currentpage;
      }
       const filters = {
          page: page,
          limit: this.limit,
      };
      this.getShipping(filters);
    },
    async getUserHistory(filters) {
      this.loading = true;
      filters.limit = 10;
      const res = await this.$server.search('userHistories', filters);
      this.loading = false;
      if (res && res.content.results) {
        this.dataHistories = res.content.results;
        for (const i in this.dataHistories) {
          this.dataHistories[i].date = this.$moment(
            this.dataHistories[i].date,
          ).format('yyyy-MM-DD  HH:mm');
        }
      } else {
        this.dataHistories = [];
      }
    },
  },
};
</script>

<style>
/* animation for vue transition tag */
dialog[open] {
  animation: appear 0.15s cubic-bezier(0, 1.8, 1, 1.8);
}
dialog::backdrop {
  background: linear-gradient(45deg, rgba(0, 0, 0, 0.5), rgba(54, 54, 54, 0.5));
  backdrop-filter: blur(3px);
}
@keyframes appear {
  from {
    opacity: 0;
    transform: translateX(-3rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
